import React, { useRef, useState } from 'react'

interface CarouselProps<T> {
  items: T[]
  renderItem: (item: T, index: number) => React.ReactNode
  showDots?: boolean
  /** Grid columns at md+. Defaults to 3 to preserve existing callers. */
  desktopCols?: 2 | 3 | 4
  /**
   * Render the grid at every breakpoint (single column below md) instead of
   * the swipe carousel. For short lists whose items are small enough to stack
   * on one phone screen — a carousel there hides every item but the first.
   */
  stackOnMobile?: boolean
}

const DESKTOP_GRID_CLASS: Record<2 | 3 | 4, string> = {
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-4'
}

function Carousel<T>({
  items,
  renderItem,
  showDots = true,
  desktopCols = 3,
  stackOnMobile = false
}: CarouselProps<T>) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchEnd, setTouchEnd] = useState<number | null>(null)
  const carouselRef = useRef<HTMLDivElement>(null)
  const minSwipeDistance = 50

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
  }

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const onTouchEnd = () => {
    if (touchStart === null || touchEnd === null) return
    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance
    if (isLeftSwipe) {
      setCurrentIndex((prev) => (prev + 1) % items.length)
    }
    if (isRightSwipe) {
      setCurrentIndex((prev) => (prev - 1 + items.length) % items.length)
    }
  }

  const goToSlide = (index: number) => {
    setCurrentIndex(index)
  }

  return (
    <>
      {/* Desktop Grid - always rendered, only visible on md+ screens (or at
          every breakpoint when stacking). */}
      <div
        className={`${stackOnMobile ? 'grid grid-cols-1' : 'hidden md:grid'} gap-6 ${DESKTOP_GRID_CLASS[desktopCols]} lg:gap-8`}
      >
        {items.map((item, index) => (
          <div key={`desktop-${index}`}>{renderItem(item, index)}</div>
        ))}
      </div>
      {/* Mobile Carousel - rendered unless stacking, only visible on <md screens */}
      {!stackOnMobile && (
        <div className="md:hidden">
          <div
            ref={carouselRef}
            className="relative overflow-hidden"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <div
              className="flex py-2 transition-transform duration-300 ease-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
              suppressHydrationWarning
            >
              {items.map((item, index) => (
                <div key={`mobile-${index}`} className="w-full shrink-0 px-2">
                  {renderItem(item, index)}
                </div>
              ))}
            </div>
          </div>
          {/* Carousel Indicators */}
          {showDots && (
            <div className="mt-3 flex justify-center space-x-2">
              {items.map((_, index) => (
                <button
                  key={`dot-${index}`}
                  onClick={() => goToSlide(index)}
                  className={`size-1.5 rounded-full transition-colors duration-300 ${
                    currentIndex === index ? 'bg-blue-500' : 'bg-grey-400'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                  suppressHydrationWarning
                />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default Carousel as <T>(props: CarouselProps<T>) => React.JSX.Element
